import ufeApi from 'services/api/ufeApi';
import urlUtils from 'utils/Url';
import userUtils from 'utils/User';
import { gqlClient } from 'services/api/gql/gqlClient';
import { PurchaseHistory } from 'constants/gql/queries/profile/purchaseHistory.gql';
import { toSortByEnum, toGroupByEnum, toPurchaseFilterEnum } from 'services/api/beautyInsider/getPurchaseHistory';

// https://jira.sephora.com/wiki/display/ILLUMINATE/Get+Purchase+History

/** @returns {boolean} Whether OIS purchase history migration is enabled */
const isOisEnabled = () =>
    !!(Sephora?.configurationSettings?.oisMigration?.global?.isEnabled &&
        Sephora?.configurationSettings?.oisMigration?.purchaseHistory?.isEnabled);

/**
 * Fetches a single page of purchase history via OXS REST.
 * @param {string} profileId - BI profile identifier
 * @param {Object} options - Query options (sortBy, itemsPerPage, currentPage, groupBy, etc.)
 * @returns {Promise<Object>} Single-page purchase history response
 */
const getPurchaseHistoryPage = async (profileId, options) => {
    if (!options) {
        throw new Error('getPurchaseHistoryPage: options parameter is required');
    }

    const biAccountId = userUtils.getBiAccountId();

    const {
        sortBy, itemsPerPage, currentPage, groupBy, excludeSamples,
        purchaseFilter, excludeRewards, includeNonBiUserPurchases
    } = options;

    const isRewardsFilter = purchaseFilter === 'rewards';

    // If filtering for rewards but no BI account exists, return default empty response.
    if (isRewardsFilter && !biAccountId) {
        return {
            profileLocale: null,
            profileStatus: null,
            purchasedItems: [],
            purchasedItemsCount: 0
        };
    }

    let url = isRewardsFilter ? `/gway/v1/lxs/${biAccountId}/redemption-history` : `/api/bi/profiles/${profileId}/purchases`;

    const qsParams = {
        sortBy,
        itemsPerPage,
        currentPage,
        groupBy,
        excludeSamples,
        purchaseFilter,
        excludeRewards,
        includeNonBiUserPurchases
    };
    const qs = urlUtils.makeQueryString(qsParams);

    if (qs) {
        url += '?' + qs;
    }

    return ufeApi.makeRequest(url, { method: 'GET', headers: { 'x-requested-source': 'web' } });
};

/**
 * Fetches complete purchase history — single OIS call or multi-page OXS aggregation.
 * @param {string} profileId - BI profile identifier
 * @param {Object} [options] - Query options (sortBy, itemsPerPage, groupBy, purchaseFilter, etc.)
 * @returns {Promise<Object>} Aggregated purchase history with all purchasedItems
 */
const getCompletePurchaseHistory = async (profileId, options = {}) => {
    const isRewardsFilter = options.purchaseFilter === 'rewards';

    // Rewards path always uses OXS — OIS does not cover the redemption-history endpoint.
    if (!isRewardsFilter && isOisEnabled()) {
        const {
            sortBy, groupBy, excludeSamples,
            purchaseFilter, excludeRewards, includeNonBiUserPurchases
        } = options;

        // locale is intentionally omitted: PurchaseHistoryInput has no locale field;
        // the OIS server derives it from the request's site_locale/site_language cookies.
        // itemsPerPage is intentionally omitted so OIS returns all items in a single call,
        // matching the "complete" contract that the OXS path fulfils via multi-page aggregation.
        const data = await gqlClient.query({
            operationName: 'PurchaseHistory',
            query: PurchaseHistory,
            variables: {
                input: {
                    profileId,
                    sortBy: toSortByEnum(sortBy),
                    groupBy: toGroupByEnum(groupBy),
                    excludeSamples,
                    purchaseFilter: toPurchaseFilterEnum(purchaseFilter),
                    excludeRewards,
                    includeNonBiUserPurchases
                }
            }
        });

        const result = data?.purchaseHistory;

        if (!result) {
            const error = new Error('Get complete purchase history failed — unexpected response shape');
            error.errorCode = -1;
            error.responseCode = 'UNEXPECTED_RESPONSE';
            error.errorMessages = ['purchaseHistory field missing from GraphQL response'];
            throw error;
        }

        if (result.responseCode && result.responseCode !== 'SUCCESS') {
            const error = new Error(result.message || 'Get complete purchase history failed');
            error.errorCode = -1;
            error.responseCode = result.responseCode;
            error.errorMessages = result.message ? [result.message] : [];
            throw error;
        }

        // Normalize: flatten purchasedGroups into purchasedItems so callers that expect
        // the flat OXS shape (e.g. fetchCompletePurchaseHistory in UserActions) need no changes.
        // groupBy: NONE returns a flat `purchasedItems` list with `purchasedGroups: null`;
        // grouped modes populate `purchasedGroups`. Prefer grouped items, fall back to flat.
        const groupedItems = (result.purchasedGroups || [])
            .flatMap(group => group.purchasedItems || []);
        const purchasedItems = groupedItems.length ? groupedItems : (result.purchasedItems || []);

        return {
            ...result,
            purchasedItems,
            purchasedItemsCount: result.purchasedItemsCount ?? purchasedItems.length
        };
    }

    // OXS path: fetch first page then all remaining pages concurrently.
    const firstPageOptions = { ...options, currentPage: 1 };
    const firstPageData = await getPurchaseHistoryPage(profileId, firstPageOptions);
    const itemsPerPage = options.itemsPerPage || 20;
    const totalItems = firstPageData.purchasedItemsCount;
    const totalPages = Math.ceil(totalItems / itemsPerPage);

    if (totalPages <= 1) {
        return firstPageData;
    }

    const promises = [];

    for (let page = 2; page <= totalPages; page++) {
        promises.push(getPurchaseHistoryPage(profileId, { ...options, currentPage: page }));
    }

    const pagesData = await Promise.all(promises);
    const allPurchasedItems = firstPageData.purchasedItems.concat(
        pagesData.reduce((acc, pageData) => acc.concat(pageData.purchasedItems), [])
    );

    return {
        profileLocale: firstPageData.profileLocale,
        profileStatus: firstPageData.profileStatus,
        purchasedItems: allPurchasedItems,
        purchasedItemsCount: allPurchasedItems.length
    };
};

export { getPurchaseHistoryPage };

export default getCompletePurchaseHistory;
